import cx from 'classnames'
import Styles from './UploadsTab.module.scss'
import { DragOverlayProps } from './Types'
import { Upload } from 'blaze-ui/icons'
import { UploadButton } from './UploadButton'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useQueryClient } from 'react-query'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import { usePress } from '@react-aria/interactions'
import { CSSProperties, FunctionComponent } from 'react'
import { ButtonProps } from 'blaze-ui/Button/Types'

interface DragOverlayForFirstUploadProps extends DragOverlayProps {
  style?: CSSProperties
  withSupportedFilesTag?: boolean
  withDropFilesTag?: boolean
  buttonProps?: Pick<ButtonProps, 'size' | 'variant' | 'onPress' | 'children' | 'isDisabled'> & { label?: string }
  button?: FunctionComponent<ButtonProps>
}

export const DragOverlayForFirstUpload = ({
  active,
  style,
  withSupportedFilesTag = true,
  withDropFilesTag = true,
  buttonProps,
  button: Button = UploadButton,
}: DragOverlayForFirstUploadProps) => (
  <div className={cx(Styles.emptyUploader, { [Styles.active]: active })} style={style}>
    <div />
    <div className={Styles.centeredVertically}>
      <div>
        <img
          src="https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1/blaze_assets/designer/nru3u9ukq2ovm9gslvj6"
          alt=""
        />
      </div>
      {withDropFilesTag && <div className={Styles.dropFilesLabel}>Drop files here or</div>}
      <div className={Styles.emptyButton}>
        <Button label="Choose Files" {...buttonProps} />
      </div>
    </div>
    {withSupportedFilesTag && (
      <div className={Styles.supportedFilesContainer}>
        Blaze supports <u>images</u> and <u>videos</u>.
      </div>
    )}
  </div>
)

export const DragOverlayForSubsequentUpload = ({ active }: DragOverlayProps) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  const uploadsProjectQuery = useUploadsProject(workspaceId)
  const uploadsProjectId = uploadsProjectQuery.data?.project?.id

  const { fileInputRef, uploadImagesFromEvent, selectInput, isUploading } = useImportMediaFiles(false, () => {
    if (uploadsProjectId) {
      queryClient.invalidateQueries(['workspace', workspaceId, 'files', uploadsProjectId])
    }
  })

  const { pressProps } = usePress({
    onPress: selectInput,
    isDisabled: !uploadsProjectId,
  })

  const handleOnChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    if (!uploadsProjectId) {
      return
    }

    uploadImagesFromEvent(event, uploadsProjectId)
  }

  return (
    <div className={cx(Styles.populatedUploader, { [Styles.active]: active })}>
      <VisuallyHidden>
        <input
          aria-label="Upload images"
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleOnChange}
          accept="image/*,video/*"
        />
      </VisuallyHidden>
      <Upload />
      {!isUploading && (
        <div className={Styles.dropFilesLabel}>
          Drop here or{' '}
          <button {...pressProps} className={cx(Styles.dropFilesLabel, Styles.chooseFilesLabel)}>
            choose files
          </button>
        </div>
      )}
      {isUploading && <div className={Styles.dropFilesLabel}>Uploading...</div>}
    </div>
  )
}
